import React, { useMemo } from 'react';
import type {
  AppliedFilter,
  ShipmentTab,
} from '@/pages/Shipments/ControlTower/types';
import CustomModalWithoutButtons from '@/components/CustomModalWithoutButtons';
import CustomTitle from '@/components/CustomTitle';
import CustomParagraph from '@/components/CustomParagraph';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';

interface Props {
  open: boolean;
  tab: ShipmentTab | null;
  onClose: () => void;
}

interface FilterRow {
  id: string;
  label?: string;
  value: string;
}

interface FilterGroup {
  label: string;
  rows: FilterRow[];
}

const getFilterText = (filter: AppliedFilter) =>
  String(filter.title || filter.name || filter.id || '').trim();

const splitDateFilterText = (text: string) => {
  const colonIndex = text.indexOf(':');
  if (colonIndex === -1) return null;

  return {
    label: text.slice(0, colonIndex).trim(),
    value: text.slice(colonIndex + 1).trim(),
  };
};

const ViewFiltersModal: React.FC<Props> = ({ open, tab, onClose }) => {
  const { formatDateTime } = useUserDateTimeFormat();
  const savedFilterSet = tab?.savedFilterSet;
  const createdAt = savedFilterSet?.createdAt;
  const createdBy = tab?.createdBy || null;

  const createdMetaText = useMemo(() => {
    const formattedDate = createdAt
      ? formatDateTime(createdAt, { includeTime: false, monthStyle: 'full' })
      : '';

    if (createdBy && createdAt) {
      return `Created by ${String(createdBy)}, ${formattedDate}`;
    }

    if (createdBy) return `Created by ${String(createdBy)}`;
    if (createdAt) return `Created ${formattedDate}`;

    return '';
  }, [createdAt, createdBy, formatDateTime]);

  const groupedFilters = useMemo<FilterGroup[]>(() => {
    const appliedFilters = savedFilterSet?.appliedFilters ?? [];
    const groups: FilterGroup[] = [];

    appliedFilters.forEach((filter, index) => {
      const text = getFilterText(filter);
      if (!text) return;

      const split = filter.dateFilter ? splitDateFilterText(text) : null;

      if (split) {
        const existingDateGroup = groups.find(
          (group) =>
            !group.label && group.rows.some((row) => row.label === split.label)
        );

        if (existingDateGroup) {
          existingDateGroup.rows.push({
            id: `${filter.id}-${index}`,
            label: split.label,
            value: split.value,
          });
        } else {
          groups.push({
            label: '',
            rows: [
              {
                id: `${filter.id}-${index}`,
                label: split.label,
                value: split.value,
              },
            ],
          });
        }

        return;
      }

      const groupLabel = String(filter.groupTitle || '').trim();

      let existingGroup = groups.find((g) => g.label === groupLabel);

      if (!existingGroup) {
        existingGroup = { label: groupLabel, rows: [] };
        groups.push(existingGroup);
      }

      existingGroup.rows.push({
        id: `${filter.id}-${index}`,
        value: text,
      });
    });

    return groups;
  }, [savedFilterSet?.appliedFilters]);

  return (
    <CustomModalWithoutButtons
      modalOpen={open}
      setModalOpen={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      modalSize={650}
      modalBorderRadius='16px'
      closeIconSize='16px'
      headerExtra={undefined}
      fontSize='34px'
      lineHeight='42px'
      className='view-filters-modal'
    >
      <div className='view-filters-modal'>
        <CustomTitle
          text={savedFilterSet?.name || 'Filter name'}
          className='view-filters-modal__title'
          fontSize='24px'
          lineHeight='32px'
          fontWeight='700'
          marginBottom='0'
        />

        {createdMetaText && (
          <CustomParagraph
            text={createdMetaText}
            className='view-filters-modal__meta'
            fontSize='12px'
            color='var(--text-secondary)'
            lineHeight='24px'
            fontWeight='400'
            marginBottom='0'
          />
        )}

        <div className='view-filters-modal__content'>
          {groupedFilters.length > 0 ? (
            groupedFilters.map((group, groupIndex) => {
              const normalRows = group.rows.filter((row) => !row.label);
              const dateRows = group.rows.filter((row) => row.label);

              const joinedNormal = normalRows
                .map((row) => row.value)
                .join(', ');

              const dateMap = new Map<string, string[]>();

              dateRows.forEach((row) => {
                const key = row.label || '';
                const existing = dateMap.get(key) || [];
                dateMap.set(key, [...existing, row.value]);
              });

              return (
                <div
                  key={`${group.label}-${groupIndex}`}
                  className='view-filters-modal__block'
                >
                  {group.label ? (
                    <CustomParagraph
                      text={group.label}
                      className='view-filters-modal__label'
                      fontSize='12px'
                      lineHeight='20px'
                      color='var(--text-secondary)'
                      fontWeight='400'
                      marginBottom='0'
                    />
                  ) : null}

                  <div className='view-filters-modal__rows'>
                    {joinedNormal ? (
                      <CustomParagraph
                        text={joinedNormal}
                        className='view-filters-modal__value'
                        fontSize='14px'
                        lineHeight='20px'
                        fontWeight='400'
                        color='black'
                        marginBottom='0'
                      />
                    ) : null}

                    {Array.from(dateMap.entries()).map(([label, values]) => (
                      <div
                        key={label}
                        className='view-filters-modal__date-block'
                      >
                        <CustomParagraph
                          text={label}
                          className='view-filters-modal__label'
                          fontSize='12px'
                          lineHeight='20px'
                          color='var(--text-secondary)'
                          fontWeight='400'
                          marginBottom='0'
                        />
                        <CustomParagraph
                          text={values.join(', ')}
                          className='view-filters-modal__value'
                          fontSize='14px'
                          lineHeight='20px'
                          fontWeight='400'
                          color='black'
                          marginBottom='0'
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <CustomParagraph
              text='No filters available'
              className='view-filters-modal__empty'
              fontSize='14px'
              lineHeight='20px'
              fontWeight='400'
              color='var(--text-secondary)'
              marginBottom='0'
            />
          )}
        </div>
      </div>
    </CustomModalWithoutButtons>
  );
};

export default ViewFiltersModal;
